/**
 * Share Text Formatters
 *
 * Formats analysis results into shareable text.
 * Features:
 * - Clean text format without emojis
 * - Caps at 3 items per section (issues, suggestions)
 * - <2K chars total
 * - Graceful truncation for long text
 * - Category-based formatting
 */
import type { DramaAnalysisResponse, DramaIssue, SuggestedRewrite } from '@dramadetector/shared';

/**
 * Maximum number of items to show per section
 */
const MAX_ITEMS_PER_SECTION = 3;

/**
 * Maximum character length for shared text
 */
const MAX_TEXT_LENGTH = 2000;

/**
 * Maximum length for a single item before truncation
 */
const MAX_ITEM_LENGTH = 100;

/**
 * Truncate text to specified length with ellipsis
 */
function truncateText(text: string, maxLength: number): string {
  if (!text || text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 3)}...`;
}

/**
 * Get category display label
 */
function getCategoryLabel(category: string): string {
  const labels: Record<string, string> = {
    safe: 'Safe',
    caution: 'Caution',
    risky: 'Risky',
  };
  return labels[category] || category;
}

/**
 * Format a single issue for sharing
 */
function formatIssue(issue: DramaIssue): string {
  const text = issue.description || issue.snippet;
  return truncateText(text, MAX_ITEM_LENGTH);
}

/**
 * Format a single suggestion for sharing
 */
function formatSuggestion(rewrite: SuggestedRewrite): string {
  const text = rewrite.reasoning || rewrite.rewritten;
  return truncateText(text, MAX_ITEM_LENGTH);
}

/**
 * Format issues section
 */
function formatIssuesSection(issues: DramaIssue[]): string {
  if (!issues || issues.length === 0) return '';

  const itemsToShow = issues.slice(0, MAX_ITEMS_PER_SECTION);
  const remaining = issues.length - itemsToShow.length;

  let section = 'Top Issues:\n';
  itemsToShow.forEach((issue) => {
    section += `• ${formatIssue(issue)}\n`;
  });

  if (remaining > 0) {
    section += `(+${remaining} more)\n`;
  }

  return section;
}

/**
 * Format suggestions section
 */
function formatSuggestionsSection(rewrites: SuggestedRewrite[]): string {
  if (!rewrites || rewrites.length === 0) return '';

  const itemsToShow = rewrites.slice(0, MAX_ITEMS_PER_SECTION);
  const remaining = rewrites.length - itemsToShow.length;

  let section = '\nSuggestions:\n';
  itemsToShow.forEach((rewrite) => {
    section += `• ${formatSuggestion(rewrite)}\n`;
  });

  if (remaining > 0) {
    section += `(+${remaining} more)\n`;
  }

  return section;
}

/**
 * Format analysis result as shareable text
 *
 * @param analysis - Drama analysis result
 * @returns Formatted text ready to share
 *
 * @example
 * ```ts
 * const text = formatShareText(analysis);
 * // Returns:
 * // Drama Analysis — "Can we talk about this?"
 * // Score: 7/10  •  Category: Caution
 * //
 * // Top Issues:
 * // • Passive-aggressive tone detected
 * // • Contains absolutes ("never", "always")
 * // • Dismissive language
 * //
 * // Suggestions:
 * // • Try using "I feel" statements
 * // • Replace absolutes with specific examples
 * // • Show empathy and understanding
 * //
 * // Shared from DramaDetector
 * ```
 */
// SDUI: share footer is server-configurable via sdui_surface_configs 'referral-share'
export function formatShareText(analysis: DramaAnalysisResponse, shareFooter?: string): string {
  if (!analysis) return '';

  // Extract title from summary (first sentence) or use generic title
  const summary = analysis.summary || '';
  const titleMatch = summary.match(/^[^.!?]+[.!?]?/);
  const title = titleMatch ? truncateText(titleMatch[0], 50) : 'Message Analysis';

  // Build header
  const score = Math.round(analysis.score ?? 0); // Score is 0-10 scale
  const category = getCategoryLabel(analysis.category ?? 'safe');
  let text = `Drama Analysis — "${title}"\n`;
  text += `Score: ${score}/10  •  Category: ${category}\n\n`;

  // Add issues section
  const issuesSection = formatIssuesSection(analysis.issues);
  text += issuesSection;

  // Add suggestions section
  const suggestionsSection = formatSuggestionsSection(analysis.rewrites);
  text += suggestionsSection;

  // Add footer
  const footer = shareFooter ?? 'Shared from DramaDetector';
  text += `\n${footer}`;

  // Truncate if necessary to stay under MAX_TEXT_LENGTH
  if (text.length > MAX_TEXT_LENGTH) {
    text = truncateText(text, MAX_TEXT_LENGTH - 30) + `\n\n${footer}`;
  }

  return text;
}
